/**
 * Trends route - Pain analytics and trends view (thin shell)
 *
 * All presentational components are extracted to @/components/trends/.
 * All compute/transform helpers are extracted to @/lib/trends/.
 * This file retains only the TrendsPage orchestration component and the route definition.
 */
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useTrendData, useEvent, useUpdateEvent, useDeleteEvent, useUserSettings } from "@/hooks";
import {
  PainTrendChart,
  InsightsPanel,
  TemperatureCorrelationCard,
  TrendsLoadingSkeleton,
  TrendsHeader,
  TrendsErrorState,
  TrendsEmptyState,
  StabilityCard,
  FlareForecastStrip,
  EventImpactStrip,
  StatisticsCards,
  CategoryImpactMatrix,
  BodyRegionBurdenCard,
  CompositeSymptomCard,
  PacingInsightCard,
  FilterBar,
  type DateRangeOption,
  buildTrendDataPoints,
  calculateAverageIntensity,
  calculateLocationFrequency,
  rollupLocationFrequencies,
  type LocationRollupMode,
  calculateTrendStatistics,
  getDateRangeStart,
  formatLocalDate,
} from "@/components/trends";
import { EventDetailDialog } from "@/components/events/event-detail-dialog";
import { getUserVisibleTags } from "@/types/pain-entry";
import type { InsightsDateRange } from "@floline/api-client";
import { computeStabilityMetrics } from "@/lib/trends/stability-metrics";
import { computeFlareForecast } from "@/lib/trends/flare-forecast";
import { maxDateRangeOption, offsetDateISO, clipSpanToRange } from "@/lib/trends/date-helpers";
import { computeEventImpacts } from "@/lib/trends/event-impact";
import { computeCategoryImpact } from "@/lib/trends/category-impact";
import { computeRegionBurden } from "@/lib/trends/region-burden";
import { buildCompositeData } from "@/lib/trends/composite-data";
import { computePacingInsight } from "@/lib/trends/pacing-insight";

const FLARE_FORECAST_LOOKBACK_DAYS = 28;
const FLARE_FORECAST_MIN_FETCH_RANGE: DateRangeOption = "30d";

type TrendsSectionTab = "overview" | "insights" | "events" | "body";

// ============================================================================
// Main Page Component
// ============================================================================

/**
 * Trends page component
 *
 * Shows pain intensity trends with event markers, statistics, and location analysis.
 */
function TrendsPage() {
  const { t, i18n } = useTranslation("trends");
  const navigate = useNavigate();
  const search = Route.useSearch();
  const activeTab: TrendsSectionTab = search.tab ?? "overview";
  const language = i18n.resolvedLanguage ?? i18n.language;

  // Locale for date formatting (e.g. "fi-FI" or "en-US")
  const locale = language.startsWith("fi") ? "fi-FI" : "en-US";

  // i18n-resolved short day names indexed by JS day-of-week (0=Sun..6=Sat)
  const forecastDayNames = useMemo(
    () => [
      t("forecast.days.sun"),
      t("forecast.days.mon"),
      t("forecast.days.tue"),
      t("forecast.days.wed"),
      t("forecast.days.thu"),
      t("forecast.days.fri"),
      t("forecast.days.sat"),
    ],
    [t]
  );

  // Trends date range selection (page-wide).
  const [dateRange, setDateRange] = useState<DateRangeOption>("30d");

  // Temperature/pain analysis range.
  // This is intentionally independent from the Trends dateRange selection so
  // the temperature relationship doesn't change when the user changes the
  // Trends time window.
  const [temperatureAnalysisRange, setTemperatureAnalysisRange] = useState<DateRangeOption>("365d");

  // Temperature overlay toggle (local state, seeded from persisted setting once).
  const [showTemperature, setShowTemperature] = useState(false);
  // Temperature unit from user settings; falls back to Celsius.
  const { settings: userSettingsData, isFetched: settingsFetched } = useUserSettings();
  const temperatureUnit = userSettingsData?.temperatureUnit ?? "c";

  // Seed showTemperature from the persisted weatherOverlayEnabled preference
  // exactly once when settings first load. After that the user controls the
  // toggle locally — we don't fight subsequent user clicks.
  const settingsSeededRef = useRef(false);
  useEffect(() => {
    if (settingsFetched && !settingsSeededRef.current && userSettingsData) {
      settingsSeededRef.current = true;
      setShowTemperature(userSettingsData.weatherOverlayEnabled);
    }
  }, [settingsFetched, userSettingsData]);

  // Weather adjustment toggle for flare forecast. Defaults ON when the
  // temperature overlay is visible, OFF otherwise.  Automatically resets
  // to OFF when the user turns off the temperature overlay so forecast
  // scores revert to the non-weather baseline.
  const [weatherForecastAdjust, setWeatherForecastAdjust] = useState(false);
  useEffect(() => {
    setWeatherForecastAdjust(showTemperature);
  }, [showTemperature]);

  // Fetch enough data to cover the page range, temperature analysis range,
  // and the flare forecast minimum window.
  const fetchDateRange = useMemo(
    () =>
      maxDateRangeOption(
        temperatureAnalysisRange,
        maxDateRangeOption(dateRange, FLARE_FORECAST_MIN_FETCH_RANGE),
      ),
    [dateRange, temperatureAnalysisRange]
  );

  // Filter state
  const [regionFilter, setRegionFilter] = useState<string | null>(null);
  const [surfaceFilter, setSurfaceFilter] = useState<string | null>(null);
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [locationRollupMode, setLocationRollupMode] = useState<LocationRollupMode>("zone");

  const setActiveTab = useCallback(
    (tab: TrendsSectionTab) => {
      navigate({
        to: "/trends",
        search: { tab },
      });
    },
    [navigate]
  );

  // Fetch trend data
  const { data, isLoading, isError, refetch } = useTrendData(fetchDateRange);

  const displayEndDate = data?.endDate ?? formatLocalDate(new Date());
  // Page-level display window (for chart, stats, stability, event impacts, etc.)
  const displayStartDate = useMemo(
    () =>
      dateRange === "all" && data?.startDate
        ? data.startDate
        : getDateRangeStart(dateRange, new Date(`${displayEndDate}T12:00:00`)),
    [dateRange, data?.startDate, displayEndDate]
  );

  // Temperature/pain analysis window (independent from Trends selection)
  const temperatureAnalysisStartDate = useMemo(
    () =>
      temperatureAnalysisRange === "all" && data?.startDate
        ? data.startDate
        : getDateRangeStart(
            temperatureAnalysisRange,
            new Date(`${displayEndDate}T12:00:00`)
          ),
    [temperatureAnalysisRange, data?.startDate, displayEndDate]
  );

  const forecastStartDate = useMemo(
    () => offsetDateISO(displayEndDate, -(FLARE_FORECAST_LOOKBACK_DAYS - 1)),
    [displayEndDate]
  );

  // Event detail dialog state
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);

  // Fetch selected event details
  const { event: selectedEvent } = useEvent(selectedEventId);

  // Event mutation hooks
  const { updateEvent, isUpdating } = useUpdateEvent();
  const { deleteEvent, isDeleting } = useDeleteEvent();

  // Handle event marker click
  const handleEventClick = useCallback((eventId: string) => {
    setSelectedEventId(eventId);
    setEventDialogOpen(true);
  }, []);

  // Handle dialog close
  const handleEventDialogClose = useCallback((open: boolean) => {
    setEventDialogOpen(open);
    if (!open) {
      setSelectedEventId(null);
    }
  }, []);

  // Handle event update
  const handleUpdateEvent = useCallback(
    async (id: string, eventData: Parameters<typeof updateEvent>[0]["data"]) => {
      await updateEvent({ id, data: eventData });
    },
    [updateEvent]
  );

  // Handle event delete
  const handleDeleteEvent = useCallback(
    async (id: string) => {
      await deleteEvent(id);
    },
    [deleteEvent]
  );

  // ---- Filter pipeline ----
  const filtersActive = regionFilter !== null || surfaceFilter !== null || tagFilter !== null;

  /** All records in the currently selected display range (no region/tag filters). */
  const displayRecordsAll = useMemo(() => {
    if (!data) return [];
    return data.records.filter(
      (r) => r.recordDate >= displayStartDate && r.recordDate <= displayEndDate
    );
  }, [data, displayStartDate, displayEndDate]);

  /** Records filtered by selected region and/or tag */
  const filteredRecords = useMemo(() => {
    if (!data || !filtersActive) return data?.records ?? [];
    return data.records.filter((record) => {
      // Region filter: keep record if any painLocation matches
      if (regionFilter) {
        const hasRegion = record.painLocations.some((loc) => (loc.zone ?? loc.region) === regionFilter);
        if (!hasRegion) return false;
      }
      // Surface filter: keep record if any painLocation matches
      if (surfaceFilter) {
        const hasSurface = record.painLocations.some((loc) => (loc.surface ?? "unknown") === surfaceFilter);
        if (!hasSurface) return false;
      }
      // Tag filter: keep record if user-visible tags include the selected tag
      if (tagFilter) {
        const userTags = getUserVisibleTags(record.tags ?? []);
        if (!userTags.includes(tagFilter)) return false;
      }
      return true;
    });
  }, [data, regionFilter, surfaceFilter, tagFilter, filtersActive]);

  /** Records within the selected display range (and filters, if active) */
  const displayRecords = useMemo(() => {
    if (!data) return [];
    const base = filtersActive ? filteredRecords : data.records;
    return base.filter(
      (r) => r.recordDate >= displayStartDate && r.recordDate <= displayEndDate
    );
  }, [data, filtersActive, filteredRecords, displayStartDate, displayEndDate]);

  /** Filtered data points derived from filtered records */
  const filteredDataPoints = useMemo(() => {
    if (!data) return [];
    return buildTrendDataPoints(displayRecords, displayStartDate, displayEndDate);
  }, [data, displayRecords, displayStartDate, displayEndDate]);

  /** Weather daily points filtered to the page display range (for the chart overlay) */
  const displayWeatherDaily = useMemo(() => {
    if (!data?.weatherDaily) return undefined;
    return data.weatherDaily.filter(
      (wp) => wp.date >= displayStartDate && wp.date <= displayEndDate
    );
  }, [data?.weatherDaily, displayStartDate, displayEndDate]);

  // ---- Temperature/pain analysis data (independent from Trends range) ----

  /** Records within the temperature analysis display range (respects filters) */
  const temperatureAnalysisRecords = useMemo(() => {
    if (!data) return [];
    const base = filtersActive ? filteredRecords : data.records;
    return base.filter(
      (r) => r.recordDate >= temperatureAnalysisStartDate && r.recordDate <= displayEndDate
    );
  }, [data, filtersActive, filteredRecords, temperatureAnalysisStartDate, displayEndDate]);

  /** Data points for the temperature analysis */
  const temperatureAnalysisDataPoints = useMemo(() => {
    if (!data) return [];
    return buildTrendDataPoints(temperatureAnalysisRecords, temperatureAnalysisStartDate, displayEndDate);
  }, [data, temperatureAnalysisRecords, temperatureAnalysisStartDate, displayEndDate]);

  /** Weather daily points filtered to the temperature analysis range */
  const temperatureAnalysisWeatherDaily = useMemo(() => {
    if (!data?.weatherDaily) return undefined;
    return data.weatherDaily.filter(
      (wp) => wp.date >= temperatureAnalysisStartDate && wp.date <= displayEndDate
    );
  }, [data?.weatherDaily, temperatureAnalysisStartDate, displayEndDate]);

  const forecastDataPoints = useMemo(() => {
    if (!data) return [];

    const base = filtersActive ? filteredRecords : data.records;
    const windowRecords = base.filter(
      (r) => r.recordDate >= forecastStartDate && r.recordDate <= displayEndDate
    );

    return buildTrendDataPoints(windowRecords, forecastStartDate, displayEndDate);
  }, [data, filtersActive, filteredRecords, forecastStartDate, displayEndDate]);

  /** Filtered location frequency derived from filtered records */
  const detailedLocationFrequency = useMemo(() => {
    if (!data) return [];
    return calculateLocationFrequency(displayRecords);
  }, [data, displayRecords]);

  const filteredLocationFrequency = useMemo(
    () => rollupLocationFrequencies(detailedLocationFrequency, locationRollupMode),
    [detailedLocationFrequency, locationRollupMode]
  );
  const mergedLocationCount = useMemo(
    () => Math.max(0, detailedLocationFrequency.length - filteredLocationFrequency.length),
    [detailedLocationFrequency.length, filteredLocationFrequency.length]
  );
  const groupingHint = useMemo(() => {
    if (locationRollupMode === "zone") {
      return t("filters.groupingHintZone", {
        defaultValue: "Combines side and surface into one row per zone.",
      });
    }
    if (locationRollupMode === "zone_side") {
      return t("filters.groupingHintZoneSide", {
        defaultValue: "Keeps left/right split, combines front/back surfaces.",
      });
    }
    return t("filters.groupingHintZoneSideSurface", {
      defaultValue: "Most detailed view: zone, side, and surface stay separate.",
    });
  }, [locationRollupMode, t]);

  /** Filtered statistics derived from filtered data points */
  const filteredStatistics = useMemo(() => {
    if (!data)
      return {
        daysWithData: 0,
        totalDays: 0,
        averageIntensity: null,
        maxIntensity: null,
        minIntensity: null,
      };
    return calculateTrendStatistics(filteredDataPoints);
  }, [data, filteredDataPoints]);

  // ---- Derived analytics (memoized, uses filtered data when filters active) ----
  const stabilityMetrics = useMemo(
    () => (data ? computeStabilityMetrics(filteredDataPoints) : null),
    [data, filteredDataPoints]
  );

  const displayEventSpans = useMemo(() => {
    if (!data) return [];
    return data.eventSpans
      .filter((span) => span.startDate <= displayEndDate && span.endDate >= displayStartDate)
      .map((span) => clipSpanToRange(span, displayStartDate, displayEndDate));
  }, [data, displayStartDate, displayEndDate]);

  // Chart-only event spans: medications filtered by showInTrends (hides chart overlays
  // without removing medications from analytics/storyline/impact sections).
  const displayChartEventSpans = useMemo(() => {
    if (!data) return [];
    return data.chartEventSpans
      .filter((span) => span.startDate <= displayEndDate && span.endDate >= displayStartDate)
      .map((span) => clipSpanToRange(span, displayStartDate, displayEndDate));
  }, [data, displayStartDate, displayEndDate]);

  const avgPainByDate = useMemo(() => {
    const map = new Map<string, number>();
    if (!data) return map;

    for (const r of data.analysisRecords) {
      const avg = calculateAverageIntensity(r.painLocations ?? []);
      if (avg !== null) {
        map.set(r.recordDate, avg);
      }
    }

    return map;
  }, [data]);

  // Events are NOT filtered by region/tag — events don't have body regions
  const eventImpacts = useMemo(
    () =>
      data
        ? computeEventImpacts(displayEventSpans, displayStartDate, displayEndDate, avgPainByDate)
        : [],
    [data, displayEventSpans, displayStartDate, displayEndDate, avgPainByDate]
  );

  const categoryImpactRows = useMemo(
    () => (data ? computeCategoryImpact(displayEventSpans, filteredDataPoints, language) : []),
    [data, displayEventSpans, filteredDataPoints, language]
  );

  const flareForecast = useMemo(
    () =>
      data
        ? computeFlareForecast(
            forecastDataPoints,
            data.eventSpans,
            locale,
            forecastDayNames,
            data.weatherDaily,
            weatherForecastAdjust
          )
        : null,
    [data, forecastDataPoints, locale, forecastDayNames, weatherForecastAdjust]
  );

  const regionBurdens = useMemo(
    () => (data ? computeRegionBurden(filteredLocationFrequency) : []),
    [data, filteredLocationFrequency]
  );

  const compositeData = useMemo(
    () => (data ? buildCompositeData(displayRecords, filteredDataPoints) : []),
    [data, displayRecords, filteredDataPoints]
  );

  const pacingInsight = useMemo(
    () => (data ? computePacingInsight(displayRecords, filteredDataPoints) : null),
    [data, displayRecords, filteredDataPoints]
  );

  // Loading state
  if (isLoading) {
    return (
      <section className="w-full">
        <TrendsHeader />
        <TrendsLoadingSkeleton />
      </section>
    );
  }

  // Error state
  if (isError) {
    return (
      <section className="w-full">
        <TrendsHeader />
        <TrendsErrorState onRetry={() => refetch()} />
      </section>
    );
  }

  // Empty state - no data at all
  if (!data || data.records.length === 0) {
    return (
      <section className="w-full">
        <TrendsHeader showDoctorVisitsLink={false} />
        <TrendsEmptyState />
      </section>
    );
  }

  return (
    <section className="w-full">
      <TrendsHeader />

      {/* Filter Bar */}
      <div className="mb-3 sm:mb-6">
        <FilterBar
          records={displayRecordsAll}
          regionFilter={regionFilter}
          surfaceFilter={surfaceFilter}
          tagFilter={tagFilter}
          onRegionChange={setRegionFilter}
          onSurfaceChange={setSurfaceFilter}
          onTagChange={setTagFilter}
          onClear={() => { setRegionFilter(null); setSurfaceFilter(null); setTagFilter(null); }}
        />
      </div>

      <div className="space-y-3 sm:space-y-6">
        {/* Pain Trend Chart -- hero section */}
        <PainTrendChart
          data={filteredDataPoints}
          eventSpans={displayChartEventSpans}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          onEventClick={handleEventClick}
          showMinMax={false}
          weatherDaily={displayWeatherDaily}
          showTemperature={showTemperature}
          onShowTemperatureChange={setShowTemperature}
          temperatureUnit={temperatureUnit}
        />

        <div className="rounded-xl border border-border/60 bg-card/40 p-2">
          <div className="flex gap-1 overflow-x-auto pb-1 sm:grid sm:grid-cols-4 sm:overflow-visible sm:pb-0">
            <button
              type="button"
              onClick={() => setActiveTab("overview")}
              aria-pressed={activeTab === "overview"}
              className={`rounded-md px-3 py-2 text-xs font-medium transition-colors ${
                activeTab === "overview"
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted/70 hover:text-foreground"
              }`}
            >
              {t("sections.overview", { defaultValue: "Overview" })}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("insights")}
              aria-pressed={activeTab === "insights"}
              className={`rounded-md px-3 py-2 text-xs font-medium transition-colors ${
                activeTab === "insights"
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted/70 hover:text-foreground"
              }`}
            >
              {t("sections.insights", { defaultValue: "Insights" })}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("events")}
              aria-pressed={activeTab === "events"}
              className={`rounded-md px-3 py-2 text-xs font-medium transition-colors ${
                activeTab === "events"
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted/70 hover:text-foreground"
              }`}
            >
              {t("sections.events", { defaultValue: "Events" })}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("body")}
              aria-pressed={activeTab === "body"}
              className={`rounded-md px-3 py-2 text-xs font-medium transition-colors ${
                activeTab === "body"
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted/70 hover:text-foreground"
              }`}
            >
              {t("sections.body", { defaultValue: "Body" })}
            </button>
          </div>
        </div>

        {activeTab === "overview" && (
          <>
            {/* Stability & Volatility Summary */}
            {stabilityMetrics && <StabilityCard metrics={stabilityMetrics} />}

            {/* Statistics Cards */}
            <StatisticsCards
              daysWithData={filteredStatistics.daysWithData}
              totalDays={filteredStatistics.totalDays}
              averageIntensity={filteredStatistics.averageIntensity}
              maxIntensity={filteredStatistics.maxIntensity}
            />
          </>
        )}

        {activeTab === "insights" && (
          <>
            {/* AI-Powered Insights */}
            <InsightsPanel
              dateRange={dateRange as InsightsDateRange}
              defaultCollapsed={false}
            />

            {/* Temperature ↔ Pain Correlation (independent from Trends selection) */}
            {showTemperature && (
              <TemperatureCorrelationCard
                dataPoints={temperatureAnalysisDataPoints}
                weatherDaily={temperatureAnalysisWeatherDaily}
                temperatureUnit={temperatureUnit}
                dateRange={temperatureAnalysisRange}
                onDateRangeChange={setTemperatureAnalysisRange}
              />
            )}

            {/* Flare Forecast Strip -- 7-day risk projection */}
            {flareForecast && (
              <FlareForecastStrip
                forecast={flareForecast}
                lookbackDays={FLARE_FORECAST_LOOKBACK_DAYS}
                weatherForecastAdjust={weatherForecastAdjust}
                onWeatherForecastAdjustChange={setWeatherForecastAdjust}
                showWeatherToggle={showTemperature && !!data?.weatherDaily}
              />
            )}

            {/* Activity Pacing Insight -- boom-bust detection */}
            {pacingInsight && <PacingInsightCard insight={pacingInsight} />}

            {/* Multi-Symptom Composite Trend */}
            {compositeData.length > 0 && (
              <CompositeSymptomCard compositeData={compositeData} />
            )}
          </>
        )}

        {activeTab === "events" && (
          <>
            {/* Event Impact Strip -- only when events exist */}
            {eventImpacts.length > 0 && (
              <EventImpactStrip
                impacts={eventImpacts}
                onEventClick={handleEventClick}
                language={language}
              />
            )}

            {/* Category Impact Matrix */}
            {categoryImpactRows.length > 0 && (
              <CategoryImpactMatrix rows={categoryImpactRows} />
            )}
          </>
        )}

        {activeTab === "body" && (
          <>
            <div className="-mt-1">
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span>{t("filters.locationGrouping", { defaultValue: "Location grouping" })}:</span>
                <button
                  type="button"
                  aria-pressed={locationRollupMode === "zone"}
                  className={`rounded px-2 py-1 transition-colors ${locationRollupMode === "zone" ? "bg-muted text-foreground font-medium ring-1 ring-border" : "hover:bg-muted/60"}`}
                  onClick={() => setLocationRollupMode("zone")}
                >
                  {t("filters.groupZone", { defaultValue: "Zone" })}
                </button>
                <button
                  type="button"
                  aria-pressed={locationRollupMode === "zone_side"}
                  className={`rounded px-2 py-1 transition-colors ${locationRollupMode === "zone_side" ? "bg-muted text-foreground font-medium ring-1 ring-border" : "hover:bg-muted/60"}`}
                  onClick={() => setLocationRollupMode("zone_side")}
                >
                  {t("filters.groupZoneSide", { defaultValue: "Zone + Side" })}
                </button>
                <button
                  type="button"
                  aria-pressed={locationRollupMode === "zone_side_surface"}
                  className={`rounded px-2 py-1 transition-colors ${locationRollupMode === "zone_side_surface" ? "bg-muted text-foreground font-medium ring-1 ring-border" : "hover:bg-muted/60"}`}
                  onClick={() => setLocationRollupMode("zone_side_surface")}
                >
                  {t("filters.groupZoneSideSurface", { defaultValue: "Zone + Side + Surface" })}
                </button>
                <span className="ml-2 text-[11px] text-muted-foreground/80">
                  {filteredLocationFrequency.length}/{detailedLocationFrequency.length}
                </span>
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground/85">
                {groupingHint}{" "}
                {mergedLocationCount > 0
                  ? t("filters.groupingMergedSuffix", {
                      count: mergedLocationCount,
                      defaultValue: "{{count}} row(s) merged by this grouping.",
                    })
                  : t("filters.groupingMergedSuffixNone", {
                      defaultValue: "No rows merged in the current filtered set.",
                    })}
              </p>
            </div>

            {/* Body Region Burden Map */}
            {regionBurdens.length > 0 && (
              <BodyRegionBurdenCard burdens={regionBurdens} groupingMode={locationRollupMode} />
            )}
          </>
        )}

      </div>

      {/* Event Detail Dialog */}
      <EventDetailDialog
        event={selectedEvent}
        open={eventDialogOpen}
        onOpenChange={handleEventDialogClose}
        onUpdate={handleUpdateEvent}
        onDelete={handleDeleteEvent}
        isUpdating={isUpdating}
        isDeleting={isDeleting}
      />
    </section>
  );
}

export const Route = createFileRoute("/_authenticated/trends")({
  component: TrendsPage,
  validateSearch: validateTrendsSearchParams,
});

export interface TrendsSearchParams {
  tab?: TrendsSectionTab;
}

function validateTrendsSearchParams(search: Record<string, unknown>): TrendsSearchParams {
  if (search.tab === "overview") return { tab: "overview" };
  if (search.tab === "insights") return { tab: "insights" };
  if (search.tab === "events") return { tab: "events" };
  if (search.tab === "body") return { tab: "body" };
  return {};
}
